import type { AccountingToday } from "@dentacore/shared-types";

import { statusLabel } from "../calendar/status-graph";
import { useModules } from "../shell/modules-context";
import { WorkLegend } from "./figures";
import { money, niceDay, plural } from "./format";
import { ProfitChart } from "./profit-chart";
import type { ChartBar } from "./profit-chart";
import styles from "./accounting.module.css";
import { PagedRows } from "../ui/paging";

/**
 * Daily — what is expected to come today (STORY-183b, DY-D1–DY-D6).
 *
 * **Every figure is the API's**; this draws it. *Expected* is the full price of
 * today's bookings but cancelled ones and no-shows; *collected* is every payment
 * received today, **including payments on older invoices** (Anthony kept that
 * simple version, DY-D2) — which is why the note under the figures says so, and
 * why *still to come* is expected minus collected and never below zero.
 *
 * The bookings list names a time, a procedure, a status and a price — never the
 * patient (DY-D6): Accounting is read by people who may not read patient files.
 */
export function DailyTab({
  data,
  currency,
  selectionLabel,
}: {
  /** Null while today is loading. */
  data: AccountingToday | null;
  currency: string;
  selectionLabel: string;
}): React.JSX.Element {
  // Without Supply there are no materials to subtract, so none are named (QF-D4).
  const hasSupply = useModules().has("SUPPLY");
  const controls = (
    <div className={styles.controls}>
      <span className={styles.strong} data-testid="accounting-daily-date">
        {data === null ? "Today" : `Today, ${niceDay(data.date)}`}
      </span>
      <p className={styles.note}>Showing {selectionLabel}.</p>
    </div>
  );

  if (data === null) {
    return (
      <>
        {controls}
        <div className={styles.card}>
          <p className={styles.blank}>Loading…</p>
        </div>
      </>
    );
  }

  const left = data.expected.count - data.finished.count;
  const bars: ChartBar[] = data.hours.map((row) => ({
    id: String(row.hour),
    label: `${String(row.hour).padStart(2, "0")}:00`,
    value: row.finishedCents,
    ghost: row.expectedCents,
    title: `${String(row.hour).padStart(2, "0")}:00: expected ${money(row.expectedCents, currency)}, finished ${money(row.finishedCents, currency)}`,
  }));

  return (
    <>
      {controls}

      <div className={styles.facts} data-testid="accounting-daily-facts">
        <div className={styles.fact}>
          <div className={styles.factKey}>Expected today</div>
          <div className={styles.factValue} data-testid="accounting-daily-expected">
            {money(data.expected.priceCents, currency)}
          </div>
          <div className={styles.factSub}>
            {plural(data.expected.count, "booking")}, not cancelled or no-show
          </div>
        </div>
        <div className={styles.fact}>
          <div className={styles.factKey}>Collected today</div>
          <div className={styles.factValue} data-testid="accounting-daily-collected">
            {money(data.collectedCents, currency)}
          </div>
          <div className={styles.factSub}>Payments received so far</div>
        </div>
        <div className={styles.fact}>
          <div className={styles.factKey}>Still to come</div>
          <div className={styles.factValue} data-testid="accounting-daily-to-come">
            {money(data.stillToComeCents, currency)}
          </div>
          <div className={styles.factSub}>
            Expected − collected, {plural(left, "booking")} not yet finished
          </div>
        </div>
        <div className={styles.fact}>
          <div className={styles.factKey}>Profit expected today</div>
          <div className={styles.factValue} data-testid="accounting-daily-profit">
            {money(data.expected.profitCents, currency)}
          </div>
          <div className={styles.factSub}>
            {hasSupply
              ? "Price − materials, for the same bookings"
              : "The price of the same bookings"}
          </div>
        </div>
      </div>

      <section className={styles.card} data-testid="accounting-daily-hours">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>Today by hour</h2>
          <span className={styles.spacer} />
          <WorkLegend expected="Expected" realized="Finished" />
        </div>
        <div className={styles.cardBody}>
          {bars.length === 0 ? (
            <p className={styles.blank} data-testid="accounting-daily-empty">
              Nothing is booked today.
            </p>
          ) : (
            <ProfitChart
              bars={bars}
              currency={currency}
              label="Expected and finished by hour, today"
              height={170}
              showValues={false}
              labelSize={10}
              testId="accounting-daily-chart"
            />
          )}
          <p className={styles.note}>
            Each booking counts in the hour it starts. Finished ones are solid; the pale part is
            still to come.
          </p>
          {data.unpriced > 0 && (
            <p className={`${styles.note} ${styles.warmNote}`}>
              {plural(data.unpriced, "booking")} {data.unpriced === 1 ? "has" : "have"} no
              procedure, so {data.unpriced === 1 ? "it counts" : "they count"} as zero.
            </p>
          )}
        </div>
      </section>

      {data.bookings.length > 0 && (
        <section className={styles.card} data-testid="accounting-daily-bookings">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Today&rsquo;s bookings</h2>
          </div>
          <PagedRows rows={data.bookings} per={12} noun="bookings">
            {(shown, offset) => (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Procedure</th>
                      <th>Status</th>
                      <th className={styles.right}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((booking, index) => (
                      <tr key={`${booking.time}-${String(offset + index)}`}>
                        <td className={styles.num}>{booking.time}</td>
                        <td>{booking.procedure ?? "No procedure"}</td>
                        <td>{statusLabel(booking.status)}</td>
                        <td className={styles.right}>{money(booking.priceCents, currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PagedRows>
        </section>
      )}

      <p className={styles.note} data-testid="accounting-daily-note">
        Collected is every payment received today, including payments on older invoices, so it can
        be more than today&rsquo;s own bookings brought in — and then it takes away from what is
        still to come.
      </p>
    </>
  );
}
